import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { applyConsent } from "../lib/tracking";

const KEY = "f2j_cookie_consent";
export const COOKIE_CONSENT_KEY = KEY;

export function getCookieConsent() {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function save(prefs) {
  const value = { version: 1, necessary: true, ...prefs, decided_at: new Date().toISOString() };
  try {
    localStorage.setItem(KEY, JSON.stringify(value));
  } catch {
    /* storage indisponível */
  }
  window.dispatchEvent(new CustomEvent("f2j:cookie-consent", { detail: value }));
  applyConsent(value);
  return value;
}

export const CookieBanner = () => {
  const [open, setOpen] = useState(false);
  const [config, setConfig] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const existing = getCookieConsent();
    if (!existing) setOpen(true);
    else applyConsent(existing);
    const openPrefs = () => {
      const cur = getCookieConsent();
      setAnalytics(!!cur?.analytics);
      setMarketing(!!cur?.marketing);
      setConfig(true);
      setOpen(true);
    };
    window.addEventListener("f2j:open-cookie-prefs", openPrefs);
    return () => window.removeEventListener("f2j:open-cookie-prefs", openPrefs);
  }, []);

  const acceptAll = () => {
    save({ analytics: true, marketing: true });
    setOpen(false);
    setConfig(false);
  };
  const rejectAll = () => {
    save({ analytics: false, marketing: false });
    setOpen(false);
    setConfig(false);
  };
  const saveChoices = () => {
    save({ analytics, marketing });
    setOpen(false);
    setConfig(false);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[60] p-3 sm:p-5 pointer-events-none">
      <div
        data-testid="cookie-banner"
        className="pointer-events-auto max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl border border-slate-200 p-5 sm:p-6"
      >
        <div className="flex items-start gap-4">
          <span className="w-10 h-10 rounded-xl bg-slate-900 text-amber-400 flex items-center justify-center shrink-0">
            <Cookie className="w-5 h-5" />
          </span>
          <div className="flex-1">
            <p className="font-bold text-slate-900">Cookies no FIRST2JOB</p>
            <p className="text-sm text-slate-600 mt-1 leading-relaxed">
              Usamos cookies estritamente necessários para o funcionamento do site e, com a tua
              autorização, cookies de análise e de marketing para melhorar a plataforma e medir campanhas.{" "}
              <Link to="/legal/politica-de-cookies" className="font-semibold text-slate-900 underline decoration-amber-400 decoration-2 underline-offset-2">
                Política de Cookies
              </Link>
            </p>

            {config && (
              <div className="mt-4 space-y-3 border-t border-slate-100 pt-4" data-testid="cookie-config">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">Estritamente necessários</p>
                    <p className="text-xs text-slate-500">Autenticação, segurança e funcionamento técnico. Sempre ativos.</p>
                  </div>
                  <Switch checked disabled aria-label="Cookies necessários (sempre ativos)" />
                </div>
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">Desempenho e análise</p>
                    <p className="text-xs text-slate-500">Estatísticas anónimas de utilização para melhoria contínua.</p>
                  </div>
                  <Switch
                    checked={analytics}
                    onCheckedChange={setAnalytics}
                    data-testid="cookie-switch-analytics"
                    aria-label="Cookies de análise"
                  />
                </div>
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">Publicidade e marketing</p>
                    <p className="text-xs text-slate-500">Medição da eficácia de campanhas e publicidade direcionada.</p>
                  </div>
                  <Switch
                    checked={marketing}
                    onCheckedChange={setMarketing}
                    data-testid="cookie-switch-marketing"
                    aria-label="Cookies de marketing"
                  />
                </div>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-2 mt-5">
              <Button data-testid="cookie-accept" onClick={acceptAll} className="sm:flex-1">
                Aceitar todos
              </Button>
              <Button data-testid="cookie-reject" variant="outline" onClick={rejectAll} className="sm:flex-1">
                Rejeitar não essenciais
              </Button>
              {config ? (
                <Button data-testid="cookie-save" variant="secondary" onClick={saveChoices} className="sm:flex-1">
                  Guardar escolhas
                </Button>
              ) : (
                <Button data-testid="cookie-configure" variant="ghost" onClick={() => setConfig(true)} className="sm:flex-1">
                  Configurar
                </Button>
              )}
            </div>
          </div>
          {getCookieConsent() && (
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Fechar"
              className="text-slate-400 hover:text-slate-700 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
